/**
 * 62.54 step 5 (ARCHITECTURE §5.6 "62.54, the design"): weather carried by the cells, behind `GameConfig.weatherCells`.
 * The wind field's cells (core/windField.ts cellsAt — the planet's keyed timeline as lows and highs drifting with the
 * bands) carry their kind's weather: the weather at a place is the cell that covers it most — rain under a low, a
 * storm under a storm cell, clear or haze under a high, clear where no cell reaches. A cell covers fully inside its
 * radius and fades to nothing at WEATHER_CELLS.edge radii, eased with its birth and fading; a **fog bank pools in low
 * ground**: its cover × how low the place lies on the planet's generated height range (the valley first, the ridge
 * last). The effect (sight, perception, hearing, wind share) blends from clear across a cell's edge — sight by its
 * reciprocal, so clear's unlimited sight blends to the cell's smoothly. Pure: dmath, keyed draws.
 */
import type { GameState, Vec3 } from '../types';
import { WEATHER_EFFECTS, type WeatherEffect, type WeatherKind } from './weather';
import { cellsAt, weatherTime } from './windField';
import { WEATHER_CELLS } from '../constants/action';
import { getSurfaceRadius } from './surface';
import { generatedRange } from './groundMaterial';
import { dacos } from './dmath';
import { vecDot, vecNormalize } from '../math';

type CellState = Pick<GameState, 'config' | 'turnNumber' | 'planets'>;

/** smoothstep from a to b. */
function smooth(a: number, b: number, x: number): number { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); }

/** How low a place lies on its planet, 0 (the highest generated ground) … 1 (the lowest): a fog bank fills the low ground first. */
export function lowness(state: CellState, planetIndex: number, dir: Vec3): number {
  const planet = state.planets[planetIndex];
  if (!planet) return 0;
  const initial = planet.initialHeightmap ?? planet.heightmap;
  const { min, range } = generatedRange(initial);
  const h = (getSurfaceRadius(planet, dir) - planet.radius - min) / range;   // 0 lowest … 1 highest generated ground
  return 1 - smooth(WEATHER_CELLS.fogPool[0], WEATHER_CELLS.fogPool[1], h);
}

/** The cell weather at a place: the kind of the cell that covers it most, and how much (0–1). Clear and 0 where none reaches. */
export function cellWeather(state: CellState, planetIndex: number, dir: Vec3, seconds = 0): { kind: WeatherKind; cover: number } {
  const n = vecNormalize(dir);
  let best: WeatherKind = 'clear', cover = 0;
  let low = -1;   // the place's lowness, found once if a fog bank asks
  for (const c of cellsAt(state, planetIndex, weatherTime(state, seconds))) {
    const cosR = Math.max(-1, Math.min(1, vecDot(c.centre, n)));
    if (cosR < c.cosReach) continue;   // beyond 4 radii, far past its edge
    const r = dacos(cosR);
    let k = c.ease * (1 - smooth(c.radius, WEATHER_CELLS.edge * c.radius, r));
    if (k <= 0) continue;
    if (c.kind === 'fog') { if (low < 0) low = lowness(state, planetIndex, n); k *= low; }
    if (k > cover) { cover = k; best = c.kind; }
  }
  return { kind: best, cover };
}

/** The weather's effect at a place under the cells: clear blended toward the covering cell's kind by its cover. */
export function cellWeatherEffect(state: CellState, planetIndex: number, dir: Vec3, seconds = 0): WeatherEffect {
  const { kind, cover } = cellWeather(state, planetIndex, dir, seconds);
  const clear = WEATHER_EFFECTS.clear, w = WEATHER_EFFECTS[kind];
  if (cover <= 0 || kind === 'clear') return clear;
  if (cover >= 1) return w;
  const inv = cover / w.sight;   // clear's sight is unlimited (1/∞ = 0): blend the reciprocal
  return {
    sight: inv > 0 ? 1 / inv : Infinity,
    perception: clear.perception + (w.perception - clear.perception) * cover,
    hearing: clear.hearing + (w.hearing - clear.hearing) * cover,
    wind: clear.wind + (w.wind - clear.wind) * cover,
  };
}
